import React, { useMemo } from 'react';
import {
  Card,
  Button,
  Space,
  Tag,
  Typography,
  Row,
  Col,
  Descriptions,
  Statistic,
  Spin,
  Alert,
  Divider,
  Collapse,
} from 'antd';
import {
  ArrowLeftOutlined,
  EditOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import { Link, useNavigate, useParams, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import api from '@/services/api.service';
import inventoryService from '@/services/inventory.service';
import { HeatStockTable } from '@/components/inventory/HeatStockTable';
import { AedDirhamSign } from '@/components/currency';
import { ProductGalleryPanel } from '@/components/inventory/ProductGalleryPanel';
import {
  MEASUREMENT_TYPE_LABELS,
  isUomCompatibleWithMeasurement,
  normalizeMeasurementType,
} from '@/utils/inventory/measurementQuantity';

const { Title, Text, Paragraph } = Typography;

function unwrap<T>(res: unknown): T {
  const data = (res as any)?.data ?? res;
  return (data?.data ?? data) as T;
}

function listBasePath(pathname: string): string {
  return pathname.startsWith('/platform') ? '/platform/foundation/items' : '/scm/inventory/items';
}

interface ItemDetail {
  id: string;
  item_code: string;
  item_name: string;
  item_description?: string | null;
  rack_number?: string | null;
  category?: { id?: string; category_name?: string } | null;
  item_type: string;
  base_uom: string;
  reorder_level?: number | null;
  reorder_quantity?: number | null;
  minimum_stock_level?: number | null;
  maximum_stock_level?: number | null;
  standard_cost?: number | null;
  standard_selling_price?: number | null;
  lead_time_days?: number | null;
  is_active?: boolean;
  is_critical?: boolean;
  is_fixed_asset?: boolean;
  heat_number_mandatory?: boolean;
  mtc_required_on_receipt?: boolean;
  profile_grade?: string | null;
  profile_heat_no?: string | null;
  profile_dimension?: string | null;
  profile_length?: string | null;
  measurement_type?: string | null;
  default_width_mm?: number | string | null;
  default_height_mm?: number | string | null;
  barcode?: string | null;
  brand?: string | null;
  specifications?: string | null;
}

interface StockRow {
  id?: string;
  item_id?: string;
  available_qty?: string | number;
  current_stock?: number;
}

const NON_STOCK_TYPES = ['Service', 'Non-Stock', 'Subcontract'];

const ItemHeatStock: React.FC<{ itemId: string }> = ({ itemId }) => {
  const { data = [], isLoading } = useQuery({
    queryKey: ['heat-wise-stock', { item_id: itemId }],
    queryFn: () => inventoryService.getHeatWiseStock({ item_id: itemId }),
  });
  return <HeatStockTable rows={data} loading={isLoading} hideItem />;
};

const ItemDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const basePath = listBasePath(location.pathname);

  const {
    data: item,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['items', 'detail', id],
    queryFn: async () => {
      const res = await api.get(`/inventory/items/${id}`);
      return unwrap<ItemDetail>(res);
    },
    enabled: !!id,
  });

  const { data: stockRows = [] } = useQuery({
    queryKey: ['items', 'stock'],
    queryFn: async () => {
      try {
        const res = await api.get('/inventory/items/stock');
        const d = unwrap<StockRow[]>(res);
        return Array.isArray(d) ? d : [];
      } catch {
        return [];
      }
    },
  });

  const currentStock = useMemo(() => {
    if (!id) return 0;
    const row = stockRows.find((r) => (r.id ?? r.item_id) === id);
    return Number(row?.available_qty ?? row?.current_stock ?? 0);
  }, [stockRows, id]);

  if (isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin size="large" />
      </div>
    );
  }

  if (isError || !item) {
    return (
      <div style={{ padding: 24 }}>
        <Alert
          type="error"
          showIcon
          message="Could not load item"
          description={(error as Error)?.message || 'Item not found'}
          action={
            <Button onClick={() => navigate(basePath)}>Back to items</Button>
          }
        />
      </div>
    );
  }

  const unitCost = Number(item.standard_cost) || 0;
  const sell = Number(item.standard_selling_price) || 0;
  const reorder = Number(item.reorder_level) || 0;
  const maxStock = Number(item.maximum_stock_level) || 0;
  const minStock = Number(item.minimum_stock_level) || 0;
  const isNonStock = NON_STOCK_TYPES.includes(item.item_type);
  const lowStock = !isNonStock && currentStock <= reorder;

  const typeColors: Record<string, string> = {
    Stock: 'blue',
    'Non-Stock': 'orange',
    Service: 'purple',
    Subcontract: 'cyan',
  };

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>
      <Space style={{ marginBottom: 20 }} wrap align="start">
        <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(basePath)}>
          Back
        </Button>
        <div>
          <Space wrap align="center" style={{ marginBottom: 4 }}>
            <Title level={2} style={{ margin: 0 }}>
              {item.item_name}
            </Title>
            {item.is_active !== false ? (
              <Tag color="success">Active</Tag>
            ) : (
              <Tag>Inactive</Tag>
            )}
            <Tag color={typeColors[item.item_type] || 'default'}>
              {item.item_type === 'Subcontract' ? 'Subcontract / Job-work' : item.item_type}
            </Tag>
            {item.is_critical ? (
              <Tag color="red">Critical</Tag>
            ) : (
              <Tag>Non-critical</Tag>
            )}
          </Space>
          <Text type="secondary" style={{ fontFamily: 'monospace' }}>
            {item.item_code}
          </Text>
        </div>
        <div style={{ flex: 1 }} />
        <Button
          type="primary"
          icon={<EditOutlined />}
          onClick={() => navigate(`${basePath}/${item.id}/edit`)}
        >
          Edit
        </Button>
      </Space>

      <Row gutter={[16, 16]}>
        <Col xs={24} lg={16}>
          <Card title="Identity & classification" style={{ marginBottom: 16 }}>
            <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Item code">{item.item_code}</Descriptions.Item>
              <Descriptions.Item label="Status">
                {item.is_active !== false ? (
                  <Tag color="success">Active</Tag>
                ) : (
                  <Tag>Inactive</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Category">
                {item.category?.category_name || '—'}
              </Descriptions.Item>
              <Descriptions.Item label="Item type">
                <Tag color={typeColors[item.item_type] || 'default'}>{item.item_type}</Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Unit of measure">{item.base_uom || '—'}</Descriptions.Item>
              <Descriptions.Item label="Lead time">
                {item.lead_time_days ?? 0} days
              </Descriptions.Item>
              <Descriptions.Item label="Brand">{item.brand || '—'}</Descriptions.Item>
              <Descriptions.Item label="Barcode">{item.barcode || '—'}</Descriptions.Item>
              <Descriptions.Item label="Description" span={2}>
                {item.item_description?.trim() || '—'}
              </Descriptions.Item>
              {item.specifications?.trim() ? (
                <Descriptions.Item label="Specifications" span={2}>
                  <Paragraph style={{ marginBottom: 0, whiteSpace: 'pre-wrap' }}>
                    {item.specifications}
                  </Paragraph>
                </Descriptions.Item>
              ) : null}
            </Descriptions>
          </Card>

          <Card title="Material profile" style={{ marginBottom: 16 }}>
            <Descriptions bordered size="small" column={{ xs: 1, sm: 2 }}>
              <Descriptions.Item label="Criticality">
                {item.is_critical ? (
                  <Tag color="red">Critical</Tag>
                ) : (
                  <Tag>Non-critical</Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label="Rack number">{item.rack_number || '—'}</Descriptions.Item>
              <Descriptions.Item label="Grade">{item.profile_grade || '—'}</Descriptions.Item>
              <Descriptions.Item label="Heat No">{item.profile_heat_no || '—'}</Descriptions.Item>
              <Descriptions.Item label="Dimension">{item.profile_dimension || '—'}</Descriptions.Item>
              <Descriptions.Item label="Length">{item.profile_length || '—'}</Descriptions.Item>
              <Descriptions.Item label="Measurement type">
                {(() => {
                  const mt = normalizeMeasurementType(item.measurement_type);
                  if (!mt) return 'Quantity only';
                  const extra = [
                    item.default_width_mm != null ? `W ${Number(item.default_width_mm)} mm` : '',
                    item.default_height_mm != null ? `H ${Number(item.default_height_mm)} mm` : '',
                  ]
                    .filter(Boolean)
                    .join(' · ');
                  return (
                    <Space size={4} wrap>
                      <span>{MEASUREMENT_TYPE_LABELS[mt]}</span>
                      {extra ? <Text type="secondary">({extra})</Text> : null}
                      {!isUomCompatibleWithMeasurement(mt, item.base_uom) ? (
                        <Tag color="orange">UOM {item.base_uom} does not fit</Tag>
                      ) : null}
                    </Space>
                  );
                })()}
              </Descriptions.Item>
              <Descriptions.Item label="Heat No mandatory on receipt">
                {item.heat_number_mandatory ? 'Yes' : 'No'}
              </Descriptions.Item>
              <Descriptions.Item label="MTC required on receipt">
                {item.mtc_required_on_receipt ? 'Yes' : 'No'}
              </Descriptions.Item>
              <Descriptions.Item label="Fixed asset from GRN">
                {item.is_fixed_asset ? 'Yes' : 'No'}
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>

        <Col xs={24} lg={8}>
          <Card
            title={
              <Space>
                <InboxOutlined />
                Stock & pricing
              </Space>
            }
            style={{ marginBottom: 16 }}
          >
            {!isNonStock && lowStock && (
              <Alert
                type="warning"
                showIcon
                style={{ marginBottom: 16 }}
                message="At or below reorder level"
              />
            )}
            <Statistic
              title="Current stock"
              value={isNonStock ? 'N/A' : currentStock}
              suffix={isNonStock ? undefined : item.base_uom}
              valueStyle={{ color: lowStock ? '#f5222d' : '#145A54', marginBottom: 16 }}
            />
            <Divider style={{ margin: '12px 0' }} />
            <Row gutter={[12, 12]}>
              <Col span={12}>
                <Statistic title="Reorder level" value={reorder} suffix={item.base_uom} />
              </Col>
              <Col span={12}>
                <Statistic title="Reorder qty" value={Number(item.reorder_quantity) || 0} />
              </Col>
              <Col span={12}>
                <Statistic title="Min stock" value={minStock} suffix={item.base_uom} />
              </Col>
              <Col span={12}>
                <Statistic title="Max stock" value={maxStock} suffix={item.base_uom} />
              </Col>
            </Row>
            <Divider style={{ margin: '16px 0' }} />
            <Statistic
              title="Standard cost"
              value={unitCost}
              prefix={<AedDirhamSign />}
              precision={2}
              style={{ marginBottom: 12 }}
            />
            <Statistic
              title="Selling price"
              value={sell}
              prefix={<AedDirhamSign />}
              precision={2}
              valueStyle={{ color: '#145A54' }}
              style={{ marginBottom: 12 }}
            />
            {!isNonStock && (
              <>
                <Divider style={{ margin: '12px 0' }} />
                <Statistic
                  title="Stock value (cost)"
                  value={currentStock * unitCost}
                  prefix={<AedDirhamSign />}
                  precision={2}
                  style={{ marginBottom: 12 }}
                />
                <Statistic
                  title="Potential value (selling)"
                  value={currentStock * sell}
                  prefix={<AedDirhamSign />}
                  precision={2}
                />
              </>
            )}
          </Card>

          {id ? (
            <Card title="Product images" size="small">
              <ProductGalleryPanel itemId={id} readOnly />
            </Card>
          ) : null}
        </Col>
      </Row>

      {id && !isNonStock ? (
        <Collapse
          style={{ marginTop: 16 }}
          destroyOnHidden
          defaultActiveKey={item.is_critical || item.heat_number_mandatory ? ['heat'] : []}
          items={[
            {
              key: 'heat',
              label: 'Stock by heat / MTC',
              extra: (
                <Link to={`/scm/inventory/heat-stock?item_id=${id}`} onClick={(e) => e.stopPropagation()}>
                  Open in Heat-wise stock
                </Link>
              ),
              children: <ItemHeatStock itemId={id} />,
            },
          ]}
        />
      ) : null}
    </div>
  );
};

export default ItemDetailPage;
